import { useSelector } from 'react-redux'
import {
    hasPermission,
    hasAllPermissions,
    hasNoPermissions,
    filterByPermissions,
    extractPermissions,
} from '../utils/permissions'
import { RootState } from '../store/store'

/**
 * Hook personalizado para acceder a los permisos del usuario desde Redux
 * y utilizar funciones de validación de permisos
 *
 * @returns Objeto con funciones para validar permisos
 */
export const usePermissions = () => {
    const userState = useSelector((state: RootState) => state.user)
    const storedUser = userState?.user
    const permissions = extractPermissions(storedUser?.role)

    return {
        // Array de permisos del usuario
        permissions,

        /**
         * Verifica si el usuario tiene al menos uno de los permisos requeridos
         */
        can: (requiredPermissions: string | string[]) => {
            const permissionsArray = Array.isArray(requiredPermissions)
                ? requiredPermissions
                : [requiredPermissions]
            return hasPermission(permissions, permissionsArray)
        },

        /**
         * Verifica si el usuario tiene TODOS los permisos requeridos
         */
        canAll: (requiredPermissions: string[]) => {
            return hasAllPermissions(permissions, requiredPermissions)
        },

        /**
         * Verifica si el usuario NO tiene ninguno de los permisos especificados
         */
        cannot: (forbiddenPermissions: string | string[]) => {
            const permissionsArray = Array.isArray(forbiddenPermissions)
                ? forbiddenPermissions
                : [forbiddenPermissions]
            return hasNoPermissions(permissions, permissionsArray)
        },

        /**
         * Filtra un array basado en permisos
         */
        filterBy: <T>(
            items: T[],
            getRequiredPermissions: (item: T) => string[] | undefined
        ) => {
            return filterByPermissions(
                items,
                permissions,
                getRequiredPermissions
            )
        },

        /**
         * Retorna el rol del usuario
         */
        role: storedUser?.role?.name,

        /**
         * Verifica si el usuario es admin
         */
        isAdmin: storedUser?.role?.name === 'Admin',
    }
}
